import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Field, Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";
import useAuthStore from "../../stores/useAuthStore";
import {
    initRegistrationFlow,
    submitPasswordRegistration,
    submitOidcFlow,
    extractCsrfToken,
    KratosError,
} from "../../services/kratos";
import { GoogleIcon, LockIcon } from "../../components/Icons/AuthIcons";
import styles from "./SignUp.module.scss";

interface SignUpFormValues {
    email: string;
    password: string;
    confirmPassword: string;
}

const initialValues: SignUpFormValues = {
    email: "",
    password: "",
    confirmPassword: "",
};

const SignUpFormikUseEffect = ({
    onValidChange,
}: {
    onValidChange: (isValid: boolean) => void;
}) => {
    const { values, errors, touched } = useFormikContext<SignUpFormValues>();
    useEffect(() => {
        const isValid = Boolean(
            values.email &&
            values.password &&
            values.confirmPassword &&
            !errors.email &&
            !errors.password &&
            !errors.confirmPassword &&
            touched.email,
        );
        onValidChange(isValid);
    }, [values, errors, touched, onValidChange]);
    return null;
};

const Register = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const session = useAuthStore((state) => state.session);

    const [disabledButton, setDisabledButton] = useState(true);
    const [apiError, setApiError] = useState("");
    const [googleLoading, setGoogleLoading] = useState(false);

    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .matches(/^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/)
            .required(),
        password: Yup.string().min(8, t("auth.register.passwordTooShort")).required(),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref("password")], t("auth.register.passwordMismatch"))
            .required(),
    });

    useEffect(() => {
        if (session) {
            navigate("/");
        }
    }, [session, navigate]);

    const handleSubmit = async (values: SignUpFormValues) => {
        setApiError("");
        try {
            const flow = await initRegistrationFlow();
            const csrfToken = extractCsrfToken(flow);
            await submitPasswordRegistration(flow.id, values.email, values.password, csrfToken);
            navigate("/auth/sign-in");
        } catch (error) {
            setApiError(translateKratosError(error));
        }
    };

    const translateKratosError = (error: unknown): string => {
        if (error instanceof KratosError) {
            return error.messageIds
                .map((id) => t(`auth.kratosErrors.${id}`, t("auth.kratosErrors.fallback")))
                .join(". ");
        }
        return error instanceof Error ? error.message : t("auth.kratosErrors.fallback");
    };

    const handleGoogleRegister = async () => {
        setApiError("");
        setGoogleLoading(true);
        try {
            const flow = await initRegistrationFlow(window.location.origin + "/auth/callback");
            const csrfToken = extractCsrfToken(flow);
            const response = await submitOidcFlow(flow.id, "google", csrfToken, "registration");
            if (response.redirect_browser_to) {
                window.location.href = response.redirect_browser_to;
                return;
            }
            navigate("/auth/sign-in");
        } catch (error) {
            setApiError(translateKratosError(error));
        } finally {
            setGoogleLoading(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
        >
            {({ errors, touched, setFieldTouched }) => (
                <Form className={styles._container}>
                    <SignUpFormikUseEffect
                        onValidChange={(isValid) => setDisabledButton(!isValid)}
                    />
                    <div className={styles.wrapper}>
                        <h1>{t("auth.register.title")}</h1>

                        <div className={styles.itemGroups}>
                            <div className={styles.itemGroup}>
                                <div
                                    className={`${styles.inputItem} ${
                                        errors.email && touched.email ? styles.inputError : ""
                                    }`}
                                >
                                    <div className={styles.labelIcon}>
                                        <Icon.Email
                                            size={22}
                                            color={
                                                errors.email && touched.email
                                                    ? "#CC1818"
                                                    : "#1D4F8B"
                                            }
                                        />
                                    </div>
                                    <div className={styles.cardContent}>
                                        <div>{t("auth.register.email")}</div>
                                    </div>
                                    <div className={styles.input}>
                                        <Field
                                            name="email"
                                            onInput={() =>
                                                setFieldTouched("email", true, true)
                                            }
                                            type="email"
                                            placeholder="E-mail"
                                        />
                                    </div>
                                </div>

                                <div
                                    className={`${styles.inputItem} ${
                                        errors.password && touched.password
                                            ? styles.inputError
                                            : ""
                                    }`}
                                >
                                    <div className={styles.labelIcon}>
                                        <LockIcon
                                            color={
                                                errors.password && touched.password
                                                    ? "#CC1818"
                                                    : "#1D4F8B"
                                            }
                                        />
                                    </div>
                                    <div className={styles.cardContent}>
                                        <div>{t("auth.register.password")}</div>
                                    </div>
                                    <div className={styles.input}>
                                        <Field
                                            name="password"
                                            onInput={() =>
                                                setFieldTouched("password", true, true)
                                            }
                                            type="password"
                                            placeholder={t("auth.register.password")}
                                        />
                                    </div>
                                </div>

                                <div
                                    className={`${styles.inputItem} ${
                                        errors.confirmPassword && touched.confirmPassword
                                            ? styles.inputError
                                            : ""
                                    }`}
                                >
                                    <div className={styles.labelIcon}>
                                        <LockIcon
                                            color={
                                                errors.confirmPassword && touched.confirmPassword
                                                    ? "#CC1818"
                                                    : "#1D4F8B"
                                            }
                                        />
                                    </div>
                                    <div className={styles.cardContent}>
                                        <div>{t("auth.register.confirmPassword")}</div>
                                    </div>
                                    <div className={styles.input}>
                                        <Field
                                            name="confirmPassword"
                                            onInput={() =>
                                                setFieldTouched("confirmPassword", true, true)
                                            }
                                            type="password"
                                            placeholder={t("auth.register.confirmPassword")}
                                        />
                                    </div>
                                </div>
                            </div>

                            {errors.confirmPassword && touched.confirmPassword && (
                                <div className={styles.inputErrorColor}>
                                    <p>{errors.confirmPassword}</p>
                                </div>
                            )}

                            {apiError && (
                                <div className={styles.apiError}>
                                    <p>{apiError}</p>
                                </div>
                            )}
                        </div>

                        <button
                            disabled={disabledButton}
                            type="submit"
                            className={`btn btn-primary ${styles.submitButton}`}
                        >
                            {t("auth.register.submit")}
                        </button>

                        <div className={styles.divider}>{t("auth.or")}</div>

                        <button
                            type="button"
                            className={styles.googleButton}
                            onClick={handleGoogleRegister}
                            disabled={googleLoading}
                        >
                            <GoogleIcon />
                            {t("auth.register.google")}
                        </button>

                        <div className={styles.linkText}>
                            {t("auth.register.hasAccount")}{" "}
                            <Link to="/auth/sign-in">{t("auth.register.login")}</Link>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default Register;
